// 図形本体（[data-testid="canvas-object-<id>"]）の実描画高さ（offsetHeight）を返すフック。
//
// テキスト図形は本文量により実描画高さが object.h（minHeight 運用）を超え得るため、
// 他参加者の選択枠とキャレット層の枠は store の h ではなく実測値を使う必要がある。
// 自分の選択枠（canvas-selection-overlay.tsx）と同じく毎レンダーの layout effect で実測し、
// store を伴わない高さ変化（フォント読み込み後の再レイアウトなど）は observeObjectLayout で追う。

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { findCanvasObjectElement } from "./canvas-object-element";
import { observeObjectLayout } from "./object-layout-observer";

/**
 * 図形本体の実描画高さ（px）。`enabled` が false、本体が無い、`offsetHeight` が 0 のときは null。
 * 値が変わったときだけ state を更新するため、毎レンダー実行でも再レンダーを誘発しない。
 */
export function useRenderedObjectHeight(objectId: string, enabled: boolean): number | null {
  const [height, setHeight] = useState<number | null>(null);
  // 観測通知を受けたときに、毎レンダーの layout effect をもう一度走らせるための契機。
  const [, bumpLayoutSignal] = useState(0);
  const observedElementRef = useRef<HTMLElement | null>(null);
  const unobserveRef = useRef<(() => void) | null>(null);

  useLayoutEffect(() => {
    const element = enabled ? findCanvasObjectElement<HTMLElement>(objectId) : null;
    if (observedElementRef.current !== element) {
      unobserveRef.current?.();
      unobserveRef.current = null;
      observedElementRef.current = element;
      if (element) {
        unobserveRef.current = observeObjectLayout(element, () =>
          bumpLayoutSignal((value) => value + 1)
        );
      }
    }
    const measured = element ? element.offsetHeight : 0;
    const next = measured > 0 ? measured : null;
    setHeight((previous) => (previous === next ? previous : next));
  });

  // アンマウント時に購読を解放する（layout effect は毎レンダー走るため、そこでは解除しない）。
  useEffect(
    () => () => {
      unobserveRef.current?.();
      unobserveRef.current = null;
      observedElementRef.current = null;
    },
    []
  );

  return height;
}
